/**
 * Masonry and directory columns and gap, per device.
 *
 * Desktop keeps the block's own `columns` and `gap`; the others store only
 * what differs, in `block.devices`. A device without its own value takes the
 * one next to it: phone from tablet, tablet from desktop, wide from desktop,
 * extra wide from wide. Wide and extra wide are screens past desktop
 * (WIDE_SCREEN), for pages laid out wider than usual. Columns taken
 * that way are capped by what the block's old width breakpoints gave at a
 * typical tablet and phone width, so a five-column desktop grid still narrows
 * on smaller screens unless a device is told otherwise.
 *
 *   block.devices = { xwide?, wide?, tablet?, phone?: { columns?, gap? } }
 *
 * Pure, so the server render, the editor and tests share it.
 */

import { getColumnCount } from '../masonry/utils.js';

/** Widest first, the order the editor lists them. */
export const GALLERY_DEVICES = ['xwide', 'wide', 'base', 'tablet', 'phone'];
export const GALLERY_COLUMNS = { min: 1, max: 6, fallback: 5 };
/** Wide screens fit more columns. */
export const GALLERY_WIDE_COLUMNS_MAX = 8;
/** Smallest window width, in px, for each screen past desktop. */
export const WIDE_SCREEN = { wide: 1800, xwide: 2400 };
export const GALLERY_GAP_FALLBACK = 4;

const TYPICAL_WIDTH = { tablet: 820, phone: 390 };
const PARENT = { tablet: 'base', phone: 'tablet', wide: 'base', xwide: 'wide' };
const WIDE = ['wide', 'xwide'];

/** Most columns a device can show. */
export function galleryColumnsMax(device) {
    return WIDE.includes(device) ? GALLERY_WIDE_COLUMNS_MAX : GALLERY_COLUMNS.max;
}

const DEFAULT_PAGE_WIDTH = 1600;

const pageWidthOf = (pageWidth) => {
    const n = Number(pageWidth);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_PAGE_WIDTH;
};

/**
 * The device a desktop window of this width is: 'xwide', 'wide' or 'base'.
 * A grid is never wider than the page, so the narrower of the two decides.
 */
export function wideDevice(width, pageWidth) {
    const n = Math.min(Number(width) || 0, pageWidthOf(pageWidth));
    if (n >= WIDE_SCREEN.xwide) return 'xwide';
    if (n >= WIDE_SCREEN.wide) return 'wide';
    return 'base';
}

/** The devices a page this wide can reach, widest first. */
export function galleryDevicesFor(pageWidth) {
    const width = pageWidthOf(pageWidth);
    return GALLERY_DEVICES.filter((device) => !WIDE_SCREEN[device] || width >= WIDE_SCREEN[device]);
}

const deviceOf = (device) => (GALLERY_DEVICES.includes(device) ? device : 'base');

const wholeColumns = (value, fallback, max = GALLERY_COLUMNS.max) => {
    const n = Math.round(Number(value));
    return Number.isFinite(n) && n > 0
        ? Math.max(GALLERY_COLUMNS.min, Math.min(max, n))
        : fallback;
};

/** The value a device stores itself, or undefined. */
function own(block, device, field) {
    const at = deviceOf(device);
    if (at === 'base') return block?.[field] ?? undefined;
    return block?.devices?.[at]?.[field];
}

/** True when a device other than desktop has its own value for `field`. */
export function galleryOverridden(block, device, field) {
    return deviceOf(device) !== 'base' && own(block, device, field) !== undefined;
}

/** Columns a device shows. */
export function galleryColumns(block, device) {
    const at = deviceOf(device);
    const base = wholeColumns(block?.columns, GALLERY_COLUMNS.fallback);
    if (at === 'base') return base;
    const mine = own(block, at, 'columns');
    if (mine !== undefined) return wholeColumns(mine, base, galleryColumnsMax(at));
    const above = galleryColumns(block, PARENT[at]);
    if (WIDE.includes(at)) return above;
    const narrowed = getColumnCount(TYPICAL_WIDTH[at], block?.breakpoints || null, base);
    return Math.max(GALLERY_COLUMNS.min, Math.min(above, narrowed));
}

/** Stored gap a device uses: its own, else the one above it. */
export function galleryGap(block, device) {
    const at = deviceOf(device);
    const mine = own(block, at, 'gap');
    if (mine !== undefined && mine !== null) return mine;
    return at === 'base' ? GALLERY_GAP_FALLBACK : galleryGap(block, PARENT[at]);
}

/** Columns and gap for every device, for renderers that pick one at runtime. */
export function galleryDeviceLayout(block) {
    return Object.fromEntries(GALLERY_DEVICES.map((device) => [
        device,
        { columns: galleryColumns(block, device), gap: galleryGap(block, device) },
    ]));
}

/**
 * Patch for one field on one device; undefined clears a non-desktop value
 * so it inherits again. Empty devices drop out of `devices`.
 * @returns {Object}
 */
export function setGalleryValue(block, device, field, value) {
    const at = deviceOf(device);
    if (at === 'base') return { [field]: value };
    const devices = { ...(block?.devices || {}) };
    const entry = { ...(devices[at] || {}) };
    if (value === undefined) delete entry[field];
    else entry[field] = value;
    if (Object.keys(entry).length) devices[at] = entry;
    else delete devices[at];
    return { devices: Object.keys(devices).length ? devices : undefined };
}
